import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1758073519996-6d3c63b4922c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGRhdGElMjB2aXN1YWxpemF0aW9uJTIwYmx1ZSUyMHRlY2h8ZW58MHx8fHwxNzg1MDE0MjA2fDA&ixlib=rb-4.1.0&q=85";

// Masked line-by-line reveal
const lineParent = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.12, delayChildren: 0.5 } },
};
const lineChild = {
  hidden: { y: "110%" },
  visible: { y: 0, transition: { duration: 1, ease: [0.22, 1, 0.36, 1] } },
};

const MaskLine = ({ children }) => (
  <span className="block overflow-hidden">
    <motion.span variants={lineChild} className="block">
      {children}
    </motion.span>
  </span>
);

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.1, 1.3]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "60%"]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-[100vh] flex items-center overflow-hidden"
    >
      {/* Parallax background */}
      <motion.div
        style={{ y: bgY, scale: bgScale }}
        className="absolute inset-0 z-0"
      >
        <img
          src={HERO_IMG}
          alt="Abstract blue data visualization"
          className="h-full w-full object-cover"
        />
      </motion.div>
      <div className="absolute inset-0 z-[1] bg-[#050A15]/75" />
      <div className="absolute inset-0 z-[1] bg-gradient-to-b from-[#050A15] via-transparent to-[#050A15]" />
      {/* Radial glow */}
      <div className="absolute left-1/2 top-1/3 z-[1] h-[500px] w-[500px] -translate-x-1/2 rounded-full bg-[#00F0FF]/10 blur-[120px]" />

      <motion.div
        style={{ y: textY, opacity: fade }}
        className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-12 w-full"
      >
        <motion.div
          variants={lineParent}
          initial="hidden"
          animate="visible"
          className="max-w-5xl"
        >
          <motion.div variants={lineChild} className="mb-8 flex items-center gap-3">
            <span className="h-px w-12 bg-[#00F0FF]" />
            <span className="text-sm uppercase tracking-[0.25em] text-[#00F0FF]">
              Business Consulting &amp; Strategic Program Development
            </span>
          </motion.div>

          <h1 className="font-display font-black tracking-tighter text-white text-5xl md:text-7xl lg:text-8xl leading-[0.95]">
            <MaskLine>Dedicated</MaskLine>
            <MaskLine>
              <span className="text-stroke-primary">software</span> that
            </MaskLine>
            <MaskLine>moves business forward.</MaskLine>
          </h1>

          <motion.p
            variants={lineChild}
            className="mt-8 max-w-xl text-lg md:text-xl font-light text-[#94A3B8] leading-relaxed"
          >
            DMAB Solutions identifies operational bottlenecks within an
            organization and delivers technology enabled solutions to streamline
            workflows, enhance efficiency, and optimize business processes.
          </motion.p>

          <motion.div variants={lineChild} className="mt-10 flex flex-wrap items-center gap-4">
            <a
              href="#contact"
              data-testid="hero-cta-primary"
              className="group inline-flex items-center gap-2 rounded-full bg-[#00F0FF] px-8 py-4 font-display font-semibold text-[#050A15] transition-all duration-300 hover:bg-[#33F3FF] hover:scale-[1.03]"
            >
              Start a project
              <ArrowUpRight
                size={18}
                className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
              />
            </a>
            <a
              href="#services"
              data-testid="hero-cta-secondary"
              className="inline-flex items-center gap-2 rounded-full border border-white/20 px-8 py-4 font-display font-medium text-white transition-all duration-300 hover:border-[#00F0FF] hover:text-[#00F0FF]"
            >
              Explore services
            </a>
          </motion.div>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-8 left-1/2 z-10 -translate-x-1/2 flex flex-col items-center gap-2 text-[#94A3B8]"
      >
        <span className="text-xs uppercase tracking-[0.25em]">Scroll</span>
        <ArrowDown size={16} className="animate-bounce" />
      </motion.div>
    </section>
  );
}
